import { diatype, diatypeItalic, diatypeMono, items } from '@ag1/fonts';
import '@ag1/ui/theme.css';
import { datadogRum } from '@datadog/browser-rum-slim';
import { GoogleTagManager } from '@next/third-parties/google';
import { GTM_ID } from '@portal/analytics/constants';
import { useHomepageTest } from '@portal/analytics/useHomepageTest';
import type { LoadHeaderDataResponse } from '@portal/processes/Content/loadHeaderData';
import { isPreviewRouteView } from '@portal/shared/utils/previewRouteHelpers';
import { ReferFriendsProvider } from '@portal/processes/ReferFriends/ReferFriendsContext';
import { BlotoutScript } from '@portal/shared/components/BlotoutScript/BlotoutScript';
import { SiteCartProvider } from '@portal/shared/components/SiteCart/hooks/SiteCartContext';
import SiteCart from '@portal/shared/components/SiteCart/SiteCart';
import { appLovinScript } from '@portal/shared/constants/appLovinScript';
import { retentionScript, suppressionScript } from '@portal/shared/constants/retentionScripts';
import { CanonicalLink } from '@portal/shared/layouts/CanonicalLink';
import { StatsigProvider } from '@portal/shared/providers/StatsigProvider';
import { TranslationProvider } from '@portal/shared/providers/TranslationProvider/TranslationProvider';
import { HeaderProviderManager } from '@portal/state/context/HeaderContext';
import { useAGZAccount } from '@portal/state/hooks/useAGZAccount';
import AfterLCP from '@portal/shared/perf/AfterLCP';
import {
  DefaultOptions,
  HydrationBoundary,
  MutationCache,
  Query,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SpeedInsights } from '@vercel/speed-insights/next';
import { AppReloadProvider } from 'appLayer/auth';
import {
  getScrollPaddingTopCSSVariable,
  scrollPaddingTopAtom,
  type ScrollPaddingTopHeaderType
} from 'atoms/scrollPaddingTopAtom';
import clsx from 'clsx';
import { Provider as JotaiProvider, useAtomValue } from 'jotai';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { CSSProperties, PropsWithChildren, useMemo, useState } from 'react';
import { ISOLocale, LocaleEnum } from 'schema/locale';
import { ketchScript } from 'shared/constants/ketch';
import InlineScript from 'shared/components/InlineScript';
import ContainerDuplicateStyles from 'shared/layouts/ContainerDuplicateStyles';
import { getLayoutPathnameFromRouter } from 'shared/layouts/useLayoutPathname';
import { NotificationContainer, showNotification } from 'shared/ui/Notification';
import { EnhancedError } from 'shared/utils/errors';
import { useAgTrackingStorage } from 'state/hooks/useAgTrackingStorage';
import { PersistStorageProvider } from 'state/persistStorage';
import { isUnidentifiedCustomerError } from '@portal/shared/utils/logging/login';
import { useAnalyticGlobalEvents } from '../analytics/global-events';
import { useAnalyticsPageView } from '../analytics/useAnalyticsPageView';
import { MILLISECONDS_IN_MINUTE } from '../constants/time-constants';
import '../i18n';
import { useCheckVersionAndRefreshApp, useStatsigExperienceAnalytics } from '../shared/hooks';
import { useViewportHeight } from '../shared/hooks/useViewportHeight';
import InitDefaults from '../shared/layouts/InitDefaults';
import { RudderAnalyticsProvider } from '../shared/providers/RudderAnalytics';
import '../styles/main.scss';
import { PageProps } from '../types/page';

const SanityLayout = dynamic(() => import('shared/layouts/SanityLayout'));
const AuthorizedLayout = dynamic(() => import('shared/layouts/AuthorizedLayout'), {
  ssr: false
});

const SierraScript = dynamic(() => import('shared/components/SierraScript/SierraScript'), {
  ssr: false,
  loading: () => null
});

const KlaviyoScript = dynamic(() => import('shared/components/KlaviyoScript/KlaviyoScript'), {
  ssr: false,
  loading: () => null
});

const LayoutWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  useAGZAccount();

  const scrollPaddingTop = useAtomValue(scrollPaddingTopAtom);
  const cssScrollPaddingTopBindings = useMemo(() => {
    const entries = Object.entries(scrollPaddingTop) as [
      ScrollPaddingTopHeaderType,
      number | undefined
    ][];

    const result: CSSProperties & Record<string, any> = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of entries) {
      if (typeof value === 'number') {
        result[getScrollPaddingTopCSSVariable(key)] = `${value}px`;
      }
    }
    return result;
  }, [scrollPaddingTop]);

  return <div style={cssScrollPaddingTopBindings}>{children}</div>;
};

const createErrorHandler = (operation: 'query' | 'mutation') => {
  const baseContext = {
    tags: {
      operation,
      handledBy: 'react-query-global-error-handler'
    }
  };

  return (error: unknown, arg: Query | unknown) => {
    let query: Query | undefined;
    if (operation === 'query') {
      query = arg as Query;
    }

    let context: object = {
      ...baseContext,
      tags: {
        ...baseContext.tags,
        ...(query ? { queryKey: query.queryKey } : {})
      }
    };

    if (error instanceof EnhancedError) {
      context = {
        ...baseContext,
        level: error.level,
        tags: {
          ...baseContext.tags,
          ...error.tags
        }
      };
    }

    // Drop expected auth failures (e.g., UNIDENTIFIED_CUSTOMER) from Datadog
    if (isUnidentifiedCustomerError(error)) {
      return;
    }
    datadogRum.addError(error, context);

    if (query?.meta?.showNotificationOnError) {
      showNotification('mutationError', {
        title: error instanceof Error ? error.message : 'Something went wrong'
      });
    }
  };
};

const App = (
  props: AppProps<
    PageProps & {
      // headerData returned from `getStaticProps` for a page.
      //
      // Used to initialize provider state for the header context.
      headerData?: LoadHeaderDataResponse;
    }
  >
) => {
  const { pageProps, router } = props;
  const { dehydratedState, store, locale, headerData } = pageProps;
  const { isCheckout, isMembers } = getLayoutPathnameFromRouter(router);

  /**
   * Indicates that this page is intended to be used within an iframe, showing a
   * preview of a specific section.
   */
  const isPreviewRoute = isPreviewRouteView(router.pathname);

  useMemo(() => {
    if (typeof window !== 'undefined') {
      window.next = window.next || {}; // Ensure next object exists
      window.next.ISOLocale = ISOLocale[locale] || 'en_US';
    }
  }, [locale]);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({ onError: createErrorHandler('query') }),
        mutationCache: new MutationCache({ onError: createErrorHandler('mutation') }),
        defaultOptions: {
          queries: {
            staleTime: MILLISECONDS_IN_MINUTE * 5,
            // Tweak retries differently in dev and prod.
            ...((): DefaultOptions['queries'] => {
              // In development, retry only once with a fixed 200ms delay. This
              // makes error states show faster in development.
              if (process.env.NODE_ENV === 'development') {
                return {
                  retry: 1,
                  retryDelay: 200
                };
              }

              // In production, retry up to 5 times with the default
              // delay (exponential backoff).
              return {
                retry: 5
              };
            })(),
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true
          }
        }
      })
  );

  useAnalyticsPageView();
  useHomepageTest();
  useAnalyticGlobalEvents();
  useViewportHeight();
  useAgTrackingStorage();
  useStatsigExperienceAnalytics();
  useCheckVersionAndRefreshApp();

  return (
    <>
      <div
        id="parent-font-container"
        className={clsx(
          items.variable,
          diatype.variable,
          diatypeItalic.variable,
          diatypeMono.variable
        )}>
        <AppReloadProvider>
          <JotaiProvider>
            <InitDefaults />
            <CanonicalLink />
            {process.env.NODE_ENV !== 'development' && <GoogleTagManager gtmId={GTM_ID} />}
            <QueryClientProvider client={queryClient}>
              <NuqsAdapter>
                <TranslationProvider>
                  <ReferFriendsProvider>
                    <HydrationBoundary state={dehydratedState}>
                      <PersistStorageProvider>
                        <StatsigProvider>
                          <RudderAnalyticsProvider>
                            <HeaderProviderManager initialHeaderData={headerData}>
                              {/* Retention pixel — not critical → lazy */}
                              <InlineScript
                                id="retention"
                                code={retentionScript}
                                strategy="lazyOnload"
                              />
                              {/* Only on checkout or members */}
                              <InlineScript
                                id="retention-suppression"
                                code={suppressionScript}
                                strategy="afterInteractive"
                                when={isCheckout || isMembers}
                              />
                              {/* Ketch — load after hydration */}
                              <InlineScript
                                id="ketch"
                                code={ketchScript}
                                strategy="afterInteractive"
                              />
                              {/* AppLovin only for marketing */}

                              <AfterLCP>
                                <InlineScript
                                  id="appLovin"
                                  code={appLovinScript}
                                  strategy="afterInteractive"
                                />
                              </AfterLCP>

                              <SiteCartProvider>
                                <SiteCart />
                                <LayoutWrapper>
                                  {isCheckout || isMembers ? (
                                    <AuthorizedLayout {...props} />
                                  ) : (
                                    <SanityLayout {...props} />
                                  )}
                                </LayoutWrapper>
                              </SiteCartProvider>

                              {!isPreviewRoute && (
                                <AfterLCP>
                                  <KlaviyoScript />
                                </AfterLCP>
                              )}
                              {!isPreviewRoute && <SierraScript />}
                              {process.env.NODE_ENV !== 'development' && (
                                <BlotoutScript store={store} locale={locale || LocaleEnum.enUS} />
                              )}
                              <SpeedInsights />
                            </HeaderProviderManager>
                          </RudderAnalyticsProvider>
                        </StatsigProvider>
                      </PersistStorageProvider>
                    </HydrationBoundary>
                    {!isPreviewRoute && (
                      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
                    )}
                  </ReferFriendsProvider>
                </TranslationProvider>
              </NuqsAdapter>
            </QueryClientProvider>
          </JotaiProvider>
        </AppReloadProvider>
      </div>
      {/*
        Container for duplicate styles
        https://github.com/vercel/next.js/issues/25456
        style in Head and style as file .css*
        In some components nad pages it can overdo each other
      */}
      <ContainerDuplicateStyles />
      {/* Should be last to prevent hydration errors */}
      <NotificationContainer />
    </>
  );
};

export default App;
